import type {UserAccountFlags} from '@frontend/types/GlobalStores'
import {create} from 'zustand/react'
import {UserAccountApi} from '../api/UserAccountApi'
import {AppConfig} from '../AppConfig'
import {CacheService} from '../services/CacheService'

// Тип состояния хранилища.
export interface AccountFlagsStoreType {
    // Данные:

    // Создавал ли пользователь объявления?
    isSeller: boolean
    // Есть ли новые уведомления?
    hasNewNotifications: boolean
    // Есть ли новые сообщения?
    hasNewChatMessages: boolean
    // Есть ли новые ответы от службы поддержки?
    hasNewSupportTicketReplies: boolean

    // API:
    // Загрузить флаги-счетчики для аккаунта пользователя.
    loadFlags: (this: void, ignoreCache?: boolean, requestConfig?: RequestInit) => Promise<void>

    // Задать флаги-счетчики для элементов бокового меню.
    setFlags: (this: void, flags: UserAccountFlags) => void

    // Изменить состояние флага наличия новых уведомлений.
    setHasNewNotifications: (this: void, value: boolean) => void

    // Изменить состояние флага наличия новых сообщений в чате.
    setHasNewChatMessages: (this: void, value: boolean) => void

    // Изменить состояние флага наличия новых ответов от службы поддержки.
    setHasNewSupportTicketReplies: (this: void, value: boolean) => void

    // Изменить состояние флага наличия объявлений у пользователя.
    setIsSeller(this: void, value: boolean): void
}

// Хранилище состояния флагов для аккаунта пользователя.
export const useAccountFlagsStore = create<AccountFlagsStoreType>(
    (set, get) => ({
        isSeller: false,
        hasNewNotifications: false,
        hasNewChatMessages: false,
        hasNewSupportTicketReplies: false,

        // API:

        async loadFlags(ignoreCache?: boolean, requestConfig?: RequestInit) {
            const cachedFlags: UserAccountFlags | null = CacheService.get<UserAccountFlags>(
                'user-flags-polling'
            )
            if (cachedFlags && !ignoreCache) {
                get().setFlags(cachedFlags)
                return Promise.resolve()
            }
            return UserAccountApi.getFlags(undefined, requestConfig)
                .then(flags => {
                    get().setFlags(flags)
                    CacheService.set(
                        'user-flags-polling',
                        flags,
                        Math.ceil((AppConfig.flagsPollingInterval * 0.8) / 60000)
                    )
                })
        },

        setFlags(flags: UserAccountFlags): void {
            set({
                isSeller: flags.isSeller,
                hasNewNotifications: flags.newNotifications,
                hasNewChatMessages: flags.newChatMessages,
                hasNewSupportTicketReplies: flags.newSupportTicketReplies,
            })
        },

        setHasNewNotifications(value: boolean): void {
            set({
                hasNewNotifications: value,
            })
        },

        setHasNewChatMessages(value: boolean): void {
            set({
                hasNewChatMessages: value,
            })
        },

        setHasNewSupportTicketReplies(value: boolean): void {
            set({
                hasNewSupportTicketReplies: value,
            })
        },

        setIsSeller(value: boolean): void {
            set({
                isSeller: value,
            })
        },
    })
)
